import {
  autoUpdate,
  FloatingNode,
  FloatingPortal,
  flip,
  offset,
  safePolygon,
  shift,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions
} from '@floating-ui/react'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import { type PropsWithChildren, type ReactElement, useState } from 'react'
import { themeClass } from '~/styles/theme.css'
import { itemGroup, itemGroupChevron, submenu } from './ActionsMenu.css'

type ActionsMenuItemGroupProps = PropsWithChildren<{
  label: string
}>

export const ActionsMenuItemGroup = ({
  label,
  children
}: ActionsMenuItemGroupProps): ReactElement => {
  const [open, setOpen] = useState(false)
  const nodeId = useFloatingNodeId()

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open,
    onOpenChange: setOpen,
    strategy: 'fixed',
    placement: 'right-start',
    middleware: [
      offset({ mainAxis: 4, crossAxis: -6 }),
      flip({ fallbackPlacements: ['left-start'] }),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate
  })

  const hover = useHover(context, {
    handleClose: safePolygon({ requireIntent: false })
  })
  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  return (
    <FloatingNode id={nodeId}>
      <div
        ref={refs.setReference}
        className={itemGroup}
        {...getReferenceProps()}
      >
        <span>{label}</span>
        <FontAwesomeIcon icon={faChevronRight} className={itemGroupChevron} />
      </div>
      <FloatingPortal>
        {open && (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(themeClass, submenu)}
            {...getFloatingProps()}
          >
            {children}
          </div>
        )}
      </FloatingPortal>
    </FloatingNode>
  )
}
